import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import GoogleWorkspaceSettings from "@/components/admin/GoogleWorkspaceSettings";
import SecuritySettings from "@/components/admin/SecuritySettings";
import ImagePolicySettings from "@/components/admin/images/ImagePolicySettings";
import RetentionApplyPanel from "@/components/admin/RetentionApplyPanel";
import LifecycleRunButtons from "@/components/admin/LifecycleRunButtons";
import TrackingHttpsPanel from "@/components/admin/sparkpost/TrackingHttpsPanel";

function fmtLastRun(lc) {
  if (!lc?.last_run_at) return "never";
  const d = new Date(lc.last_run_at);
  const when = isNaN(d) ? lc.last_run_at : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  const r = lc.last_run;
  return r ? `${when} · archived ${r.archived} · purged ${r.purged} · ${r.tombstones_deleted} tombstones (${r.trigger})` : when;
}

function Badge({ state, children }) {
  const c = state === "live"
    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
    : state === "error"
    ? "bg-red-500/10 text-red-400 border-red-500/30"
    : state === "partial"
    ? "bg-blue-500/10 text-blue-400 border-blue-500/30"
    : "bg-amber-500/10 text-amber-400 border-amber-500/30";
  return (
    <span className={`inline-block border ${c} mono text-[11px] uppercase tracking-wider px-2 py-1 rounded-sm`}>{children}</span>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-zinc-800 rounded-sm">
      <div className="px-5 py-3 border-b border-zinc-800 label-tiny">{title}</div>
      <div className="divide-y divide-zinc-800">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="px-5 py-3 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-3 items-center">
      <div className="text-sm text-zinc-300">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function NamecheapBadge({ nc }) {
  if (!nc) return <Badge state="mocked">CHECKING…</Badge>;
  if (nc.ok && nc.live) return <Badge state="live">LIVE · {nc.env || "production"} · IP {nc.client_ip}</Badge>;
  if (nc.configured && !nc.ok) return <Badge state="error">CONFIGURED but FAILING · {nc.error_code || ""} {nc.error || ""}</Badge>;
  return (
    <div className="flex flex-col gap-1">
      <Badge state="partial">PARTIAL · API key ✓ · API user ✗ · username ✗ · IP {nc.client_ip || "?"} ✓</Badge>
      <div className="text-[11px] text-amber-400 mono">waiting on NAMECHEAP_API_USER + NAMECHEAP_USERNAME in .env</div>
    </div>
  );
}

function CloudflareBadge({ cf }) {
  if (!cf) return <Badge state="mocked">CHECKING…</Badge>;
  if (cf.ok && cf.live) return <Badge state="live">LIVE · token {cf.token_status} · acct {(cf.account_id || "").slice(0, 12)}…</Badge>;
  if (cf.configured && !cf.ok) return <Badge state="error">CONFIGURED but FAILING · {cf.error_code || ""} {cf.error || ""}</Badge>;
  return <Badge state="mocked">MOCKED · waiting for CLOUDFLARE_API_TOKEN + CLOUDFLARE_ACCOUNT_ID</Badge>;
}

function EmailRoutingBadge({ dest }) {
  if (!dest) return <Badge state="mocked">CHECKING…</Badge>;
  if (!dest.admin_destination) return <Badge state="partial">No ADMIN_DESTINATION_EMAIL set in .env</Badge>;
  const cur = dest.current;
  if (cur && cur.verified) return <Badge state="live">LIVE · {dest.admin_destination} · verified</Badge>;
  if (cur && !cur.verified) return <Badge state="partial">PENDING VERIFICATION · check {dest.admin_destination} inbox</Badge>;
  return <Badge state="partial">Destination not registered yet · click any site&apos;s Email tab to bootstrap</Badge>;
}

export default function SettingsPage() {
  const [nc, setNc] = useState(null);
  const [cf, setCf] = useState(null);
  const [dest, setDest] = useState(null);
  const [ts, setTs] = useState(null);
  const [r2, setR2] = useState(null);
  const [lc, setLc] = useState(null);
  const [wk, setWk] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wkMsg, setWkMsg] = useState("");
  const reloadLc = () => api.get("/admin/lifecycle/status").then(r => setLc(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/sites/domain/status").then(r => setNc(r.data)).catch(() => setNc({ configured: false, ok: false, error: "endpoint unreachable" }));
    api.get("/sites/cloudflare/status").then(r => setCf(r.data)).catch(() => setCf({ configured: false, ok: false, error: "endpoint unreachable" }));
    api.get("/email/destinations").then(r => setDest(r.data)).catch(() => setDest(null));
    api.get("/public/forms/turnstile-status").then(r => setTs(r.data)).catch(() => setTs({ configured: false }));
    api.get("/sites/r2/status").then(r => setR2(r.data)).catch(() => setR2({ configured: false, ok: false }));
    api.get("/admin/lifecycle/status").then(r => setLc(r.data)).catch(() => setLc(null));
    api.get("/admin/worker/status").then(r => setWk(r.data)).catch(() => setWk({ configured: false }));
  }, []);

  async function deployWorker() {
    setBusy(true); setWkMsg("");
    try {
      const r = await api.post(`/admin/worker/deploy`, {});
      setWkMsg(r.data.ok ? `Deployed · script=${r.data.script}` : `Failed: ${JSON.stringify(r.data.errors)}`);
      const s = await api.get(`/admin/worker/status`);
      setWk(s.data);
    } catch (e) {
      setWkMsg(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="font-display text-3xl font-black tracking-tight">Platform Settings</h1>
      <div className="text-zinc-500 text-sm mt-1">
        Global ops settings. Most per-site config lives under each site&apos;s Settings tab.
      </div>

      <div className="mt-8 space-y-4">
        <SecuritySettings />

        <ImagePolicySettings />

        <Section title="API Integrations">
          <Row label="Namecheap"><NamecheapBadge nc={nc} /></Row>
          <Row label="Cloudflare"><CloudflareBadge cf={cf} /></Row>
          <Row label="Email Routing (Cloudflare)"><EmailRoutingBadge dest={dest} /></Row>
          <Row label="Cloudflare R2">
            {r2 === null ? <Badge state="mocked">CHECKING…</Badge>
              : r2.ok ? <Badge state="live">LIVE · bucket <span className="mono">{r2.bucket}</span> · {r2.public_base ? "direct CDN" : "via /api/public/img proxy (paste R2_PUBLIC_BASE for direct CDN)"}</Badge>
              : r2.configured ? <Badge state="error">CONFIGURED but FAILING · {r2.error}</Badge>
              : <Badge state="mocked">MOCKED · waiting for R2_* env vars</Badge>}
          </Row>
          <Row label="Amazon SES (bulk newsletters)"><Badge state="mocked">MOCKED · waiting for AWS keys + verified domain</Badge></Row>
          <Row label="Postmark (transactional)"><Badge state="mocked">MOCKED · waiting for server token</Badge></Row>
          <Row label="Infobip SMS"><Badge state="mocked">MOCKED · waiting for API key</Badge></Row>
          <Row label="Facebook / Threads / LinkedIn / Bluesky"><Badge state="mocked">MOCKED · social posting deferred to Slice 5</Badge></Row>
          <Row label="Google Postmaster Tools"><Badge state="mocked">MOCKED · waiting for GCP service account</Badge></Row>
          <Row label="Emergent LLM (Claude Sonnet 4.6)"><Badge state="live">LIVE · real AI rewrite enabled</Badge></Row>
          <Row label="News API keys"><Badge state="live">LIVE · RSS + NewsData + AlphaVantage + Finnhub</Badge></Row>
        </Section>

        <Section title="Email tracking links (SparkPost HTTPS)">
          <Row label="Tracking domains (all sites)"><TrackingHttpsPanel /></Row>
        </Section>

        <GoogleWorkspaceSettings />

        <Section title="Compliance">
          <Row label="Cookie consent banner">Enabled on all public sites</Row>
          <Row label="Turnstile bot protection">
            {ts === null ? <Badge state="mocked">CHECKING…</Badge>
              : ts.configured ? <Badge state="live">LIVE · site key {(ts.site_key || "").slice(0, 8)}…</Badge>
              : <Badge state="mocked">MOCKED · waiting for TURNSTILE_SECRET + TURNSTILE_SITE_KEY in .env</Badge>}
          </Row>
          <Row label="Physical address in email footers"><span className="text-amber-500 text-sm">⚠️ Not configured — CAN-SPAM requires this when newsletters launch (Slice 4)</span></Row>
        </Section>

        <Section title={`Article lifecycle (${lc?.defaults?.hot_days ?? 14} live → ${lc?.defaults?.archive_days ?? 14} archived → 410 → gone)`}>
          <Row label="Daily cron">
            {lc === null ? <Badge state="mocked">CHECKING…</Badge>
              : <Badge state="live">LIVE · runs at {String(lc.cron_hour_utc).padStart(2,"0")}:00 UTC · <span data-testid="lifecycle-last-run">last run: {fmtLastRun(lc)}</span></Badge>}
          </Row>
          <Row label="Retention window (all sites)">
            <RetentionApplyPanel status={lc} onDone={reloadLc} />
          </Row>
          <Row label="Manual run">
            <LifecycleRunButtons onDone={reloadLc} />
          </Row>
        </Section>

        <Section title="Cloudflare Worker SSR">
          <Row label="Worker status">
            {wk === null ? <Badge state="mocked">CHECKING…</Badge>
              : !wk.configured ? <Badge state="mocked">Token not set — add CLOUDFLARE_DEPLOY_TOKEN to .env</Badge>
              : wk.deployed ? <Badge state="live">LIVE · script <span className="mono">{wk.script_name}</span> deployed</Badge>
              : <Badge state="partial">Token configured · script not yet uploaded</Badge>}
          </Row>
          <Row label="Deploy / re-deploy">
            <div className="flex gap-2 items-center flex-wrap">
              <button data-testid="worker-deploy" disabled={busy} onClick={deployWorker} className="px-3 py-1.5 text-xs border border-emerald-700 text-emerald-300 rounded-sm hover:border-emerald-400 disabled:opacity-50">{wk?.deployed ? "Re-upload worker" : "Deploy worker"}</button>
              {wkMsg && <span className="text-xs text-zinc-400 mono">{wkMsg}</span>}
            </div>
          </Row>
        </Section>

        <Section title="Architecture Notes">
          <div className="text-sm text-zinc-400 space-y-2 px-5 py-3">
            <p>This is the Slice 1 build. Public sites are currently rendered as React routes on this same Emergent app for preview. In Slice 3 we wire up Cloudflare Workers SSR per custom domain.</p>
            <p className="text-xs text-zinc-500">Admin: Emergent hosting · Backend: FastAPI + MongoDB · Public sites (future): Cloudflare Workers + edge cache</p>
          </div>
        </Section>
      </div>
    </div>
  );
}
